import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  EntityType,
  KindType,
  LanguageType
} from '@/__generated__/gateway/graphql';
import { entityTypes } from '@/shared/constants/product';
import {
  queryPatternBySku,
  queryTabCounters
} from '@/shared/graphql/gateway/queries';
import { useProductQuantitiesStore } from '@/shared/pinia/modules/product-quantities';
import { productStoreInternals } from '@/shared/pinia/modules/product-utils';
import { useUserStore } from '@/shared/pinia/modules/user';
import { Strategy } from '@/shared/strategies/index';
import { getApolloClients } from '@/shared/utils/apollo-client';
import { notifyBugsnagWithContext } from '@/shared/utils/error';
import { getLanguageType } from '@/shared/utils/gateway/helpers';

export type ProductPrice = {
  initial_unit: number | null;
  unit: number | null;
  before: number | null;
  savings: number | null;
};

export const useProductStore = defineStore('product', () => {
  const productId = ref<string | null>(null);
  const productName = ref<string | null>(null);
  const variantId = ref<string | null>(null);
  const legacyId = ref<string | null>(null);
  const strategy = ref<string>('');
  const purchasable = ref(true);
  const entityType = ref(entityTypes.PRODUCT);
  const mainImageUrl = ref<string | null>(null);
  const pointsPrice = ref<number | null>(null);
  const price = ref<ProductPrice>({
    initial_unit: null, // unit price without discount
    unit: null,
    before: null,
    savings: null
  });

  const {
    updateDOMPricesAndSavings,
    calculateNewPricesByQuantityChange,
    calculateNewPricesByVariantChange
  } = productStoreInternals();

  const isPattern = computed(() => entityType.value === entityTypes.PATTERN);
  const isProduct = computed(() => entityType.value === entityTypes.PRODUCT);
  const getStrategy = computed(() => strategy.value || Strategy.ProductPage);
  const isPointProduct = computed(
    () => !!pointsPrice.value && pointsPrice.value > 0
  );

  const setStrategy = (strategyValue: string) => {
    const strategyEntries = Object.entries(Strategy);
    const foundEntry = strategyEntries.find(
      ([, value]) => value === strategyValue
    );

    if (foundEntry) {
      strategy.value = foundEntry[1];
    }
  };

  const updatePriceByQuantity = () => {
    const productQuantitiesStore = useProductQuantitiesStore();
    if (!productQuantitiesStore.bulkDiscountsAvailable) return;

    calculateNewPricesByQuantityChange();
    updateDOMPricesAndSavings();
  };

  const updatePriceByVariant = (variant: HydratedShopifyVariant) => {
    const quantityStore = useProductQuantitiesStore();

    if (quantityStore.bulkDiscountsAvailable) {
      calculateNewPricesByQuantityChange();
      updateDOMPricesAndSavings();
    } else {
      calculateNewPricesByVariantChange(variant);
      updateDOMPricesAndSavings();
    }
  };

  const setTabCounters = async () => {
    const entity = window.Hobbii?.productPage?.isPattern
      ? EntityType.Pattern
      : EntityType.Product;

    const entity_id = legacyId.value || productId.value;

    const locale = window.Shopify?.locale ?? 'en';
    const language = getLanguageType(locale, LanguageType.En);
    const queryArgs = {
      galleryContext: {
        kind: KindType.Gallery,
        entity,
        entity_id,
        language
      },
      qaContext: {
        kind: KindType.Qa,
        entity,
        entity_id,
        language
      }
    };

    const { gateway } = await getApolloClients();
    return gateway.query(queryTabCounters(queryArgs));
  };
  const patternDownloadUrl = async (
    productSku: string,
    contactUUID: string,
    countryCode: string
  ) => {
    const userStore = useUserStore();
    if (purchasable.value && !userStore.isLogged) return null;
    if (!contactUUID && !userStore.isLogged) {
      notifyBugsnagWithContext(
        'queryPatternBySku',
        `[DATA-ERROR] - Pattern download will not be tracked. No contactUUID and no session.`
      );
    }

    try {
      const { gateway } = await getApolloClients();
      const patternBySkuResponse = await gateway.query(
        queryPatternBySku(productSku, window.Shopify.locale, undefined, {
          contactUUID: contactUUID ?? undefined,
          countryCode
        })
      );

      return patternBySkuResponse.data?.patternBySku?.url ?? null;
    } catch (error) {
      notifyBugsnagWithContext('patternDownloadUrl', error, {
        message: `[DATA-ERROR] - Error on queryPatternBySku (SKU: "${productSku}", countryCode: "${countryCode}")`
      });

      return null;
    }
  };

  return {
    purchasable,
    isProduct,
    isPattern,
    productId,
    productName,
    legacyId,
    price,
    entityType,
    variantId,
    pointsPrice,
    mainImageUrl,
    setTabCounters,
    patternDownloadUrl,
    getStrategy,
    setStrategy,
    isPointProduct,
    updatePriceByVariant,
    updatePriceByQuantity
  };
});
